import { Link } from 'react-router';
import { Badge } from '@xiltrion/ui/badge';
import { Button } from '@xiltrion/ui/button';
import { Icono } from '@xiltrion/ui/icono';
import { Progress } from '@xiltrion/ui/progress';
import { SETUP_PRESETS, SETUP_STEPS } from '@pqrs/contracts';
import { PageHeader } from '@/components/ui';
import { useSession } from '@/app/session';
import { setupSteps, useSetupStatus } from '@/features/setup/api';
import { useConfigSections } from './ConfigLayout';
import { Help } from './shared';

/** Avance de la puesta en marcha (o, en operación, lo que dejó de cumplirse), arriba del resumen. */
function SetupSummary() {
  const q = useSetupStatus();
  if (!q.data) return null;
  const steps = setupSteps(q.data.checks);
  const done = steps.filter((s) => s.done).length;
  const missing = q.data.checks.filter((c) => c.level === 'required' && !c.ok).length;
  const setup = q.data.status === 'SETUP';
  if (!setup && !missing) return null;
  return (
    <section aria-labelledby="hub-setup" className="mb-6 bg-card border border-border rounded-lg p-4 flex flex-col gap-3 sm:flex-row sm:items-center" style={{ boxShadow: 'var(--elev-1)' }}>
      <span className="w-10 h-10 rounded-md bg-primary/10 text-primary inline-flex items-center justify-center text-2xl shrink-0" aria-hidden><Icono nombre="mdi:clipboard-list-outline" /></span>
      <div className="flex-1 min-w-0 space-y-2">
        <h2 id="hub-setup" className="text-base font-medium m-0">{setup ? 'Puesta en marcha' : 'Requisitos sin cumplir'}</h2>
        <p className="text-sm text-muted-foreground m-0">
          {setup ? `${done} de ${steps.length} pasos completos. La entidad no recibe solicitudes hasta pasar a operación.` : `${missing === 1 ? 'Un requisito obligatorio dejó' : `${missing} requisitos obligatorios dejaron`} de cumplirse.`}
        </p>
        {setup && <Progress value={Math.round((done / steps.length) * 100)} aria-label="Avance de la puesta en marcha" />}
      </div>
      <Button asChild><Link to="/puesta-en-marcha">{setup ? 'Continuar' : 'Revisar'}</Link></Button>
    </section>
  );
}

/** Configuration hub. Cards per section. */
export function ConfigHub() {
  const { admin } = useSession();
  const setup = useSetupStatus(admin);
  // Requisitos obligatorios sin cumplir por pantalla de Configuración (la del paso que los resuelve).
  const pendingBySection = new Map<string, number>();
  for (const c of setup.data?.checks ?? []) {
    const path = [...SETUP_STEPS, ...SETUP_PRESETS].find((st) => st.key === c.step)?.path;
    if (path && c.level === 'required' && !c.ok) pendingBySection.set(path, (pendingBySection.get(path) ?? 0) + 1);
  }
  const sections = useConfigSections();
  const groups = Array.from(new Set(sections.map((s) => s.group)));
  return (
    <div>
      <PageHeader title="Configuración" subtitle="Todo lo que define el comportamiento de la plataforma se configura aquí, sin programar." />
      {admin && <SetupSummary />}
      {groups.map((g) => {
        const items = sections.filter((s) => s.group === g);
        if (!items.length) return null;
        return (
          <section key={g} className="mb-6" aria-labelledby={`hub-${g}`}>
            <h2 id={`hub-${g}`} className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-2">{g}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              {items.map((s) => (
                <Link key={s.to} to={`/configuracion/${s.to}`} className="no-underline text-inherit bg-card border border-border rounded-lg p-4 flex gap-3 hover:border-primary hover:shadow focus-visible:border-primary transition-colors" style={{ boxShadow: 'var(--elev-1)' }}>
                  <span className="w-10 h-10 rounded-md bg-primary/10 text-primary inline-flex items-center justify-center text-2xl shrink-0" aria-hidden><Icono nombre={s.icon} /></span>
                  <span className="min-w-0 flex-1">
                    <span className="font-medium flex items-center gap-2">{s.label}{pendingBySection.get(`/configuracion/${s.to}`) ? <Badge variant="destructive-light" size="sm">Pendiente</Badge> : null}</span>
                    <span className="text-sm text-muted-foreground">{s.description}</span>
                  </span>
                </Link>
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}
